import { Button, Card, Text, Divider, Group, Stack, Title, LoadingOverlay } from '@mantine/core'
import { Slot } from '@medplum/fhirtypes'
import { useMemo, useState } from 'react'

function isToday(date: Date): boolean {
  const today = new Date()
  return (
    date.getDate() === today.getDate() &&
    date.getMonth() === today.getMonth() &&
    date.getFullYear() === today.getFullYear()
  )
}
import classes from './CalendarInput.module.css'
import { getMonthString, getStartMonth } from './CalendarInput.utils'
import { IconChevronLeft, IconChevronRight } from '@tabler/icons-react'

export interface CalendarInputProps {
  slots: Slot[] | []
  onChangeMonth: (date: Date) => void
  onClick: (date: Date) => void
  onBack: () => void
  defaultMonth?: Date
  isLoadingSlots?: boolean
}

interface CalendarCell {
  readonly date: Date
  readonly available: boolean
}

type OptionalCalendarCell = CalendarCell | undefined

export function CalendarInput(props: CalendarInputProps) {
  const { onChangeMonth, onClick, defaultMonth, onBack, isLoadingSlots } = props
  const [month, setMonth] = useState<Date>(defaultMonth || getStartMonth())

  function moveMonth(delta: number): void {
    setMonth((currMonth) => {
      const newMonth = new Date(currMonth.getTime())
      newMonth.setMonth(currMonth.getMonth() + delta)
      onChangeMonth(newMonth)
      return newMonth
    })
  }

  const grid = useMemo(() => buildGrid(month, props.slots), [month, props.slots])

  return (
    <Card
      radius="xl"
      p="xl"
      withBorder
      style={{
        width: 460,
        margin: '0 auto',
        borderColor: 'rgba(34, 139, 230, 0.18)',
        background:
          'radial-gradient(1200px 600px at 20% -10%, rgba(34,139,230,0.14) 0%, transparent 55%),' +
          'radial-gradient(900px 500px at 110% 10%, rgba(99,102,241,0.10) 0%, transparent 55%),' +
          'linear-gradient(180deg, rgba(255,255,255,0.92) 0%, rgba(255,255,255,0.98) 100%)',
        boxShadow: '0 20px 45px rgba(0,0,0,0.08), 0 2px 10px rgba(0,0,0,0.05)',
      }}
    >
      <Group justify="flex-start" align="flex-start">
        <Stack gap={4}>
          <Title order={2} style={{ textAlign: 'left', lineHeight: 1.1 }}>
            Choose the date
          </Title>
          <Text c="dimmed" size="sm" style={{ textAlign: 'left' }}>
            Select one from available dates.
          </Text>
        </Stack>
      </Group>
      <Divider my="lg" />
      <LoadingOverlay visible={isLoadingSlots} />
      <div className="flex items-center justify-between">
        <Button variant="transparent" className="!pl-0" onClick={() => moveMonth(-1)}>
          <IconChevronLeft />
        </Button>
        <p className="flex-1 text-center text-sm font-bold">{getMonthString(month)}</p>
        <Button variant="transparent" className="!pl-0" onClick={() => moveMonth(1)}>
          <IconChevronRight />
        </Button>
      </div>
      <table className={classes.table}>
        <thead>
          <tr>
            <th className="!text-[16px] text-[#868E96]">Su</th>
            <th className="!text-[16px] text-[#868E96]">Mo</th>
            <th className="!text-[16px] text-[#868E96]">Tu</th>
            <th className="!text-[16px] text-[#868E96]">We</th>
            <th className="!text-[16px] text-[#868E96]">Th</th>
            <th className="!text-[16px] text-[#868E96]">Fr</th>
            <th className="!text-[16px] text-[#868E96]">Sa</th>
          </tr>
        </thead>
        <tbody>
          {grid.map((week, weekIndex) => (
            <tr key={'week-' + weekIndex}>
              {week.map((day, dayIndex) => (
                <td key={'day-' + dayIndex}>
                  {day && (
                    <Button
                      variant="ghost"
                      className={[
                        '!rounded-sm',
                        '!text-[16px]',
                        !day.available ? '!text-[#868E96]' : 'text-black',
                        isToday(day.date) ? '!border-brand-green !border-1' : '',
                      ].join(' ')}
                      disabled={!day.available}
                      onClick={() => onClick(day.date)}
                    >
                      {day.date.getDate()}
                    </Button>
                  )}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      <Divider my="lg" />
      <Group mt="md">
        <Button variant="outline" onClick={onBack}>
          Cancel
        </Button>
      </Group>
    </Card>
  )
}

function buildGrid(startDate: Date, slots: Slot[]): OptionalCalendarCell[][] {
  const d = new Date(startDate.getFullYear(), startDate.getMonth())
  const grid: OptionalCalendarCell[][] = []
  let row: OptionalCalendarCell[] = []

  // Fill leading empty days
  for (let i = 0; i < d.getDay(); i++) {
    row.push(undefined)
  }

  while (d.getMonth() === startDate.getMonth()) {
    row.push({
      date: new Date(d.getTime()),
      available: isDayAvailable(d, slots),
    })

    if (d.getDay() === 6) {
      grid.push(row)
      row = []
    }

    d.setDate(d.getDate() + 1)
  }

  // Fill trailing empty days
  if (d.getDay() !== 0) {
    for (let i = d.getDay(); i < 7; i++) {
      row.push(undefined)
    }
    grid.push(row)
  }

  return grid
}

/**
 * Returns true if the given date is available for booking.
 * @param day - The day to check.
 * @param slots - The list of available slots.
 * @returns True if there are any available slots for the day.
 */
function isDayAvailable(day: Date, slots: Slot[]): boolean {
  // Note that slot start and end time may or may not be in UTC.
  for (const slot of slots) {
    const slotStart = new Date(slot.start)
    if (
      slotStart.getFullYear() === day.getFullYear() &&
      slotStart.getMonth() === day.getMonth() &&
      slotStart.getDate() === day.getDate()
    ) {
      return true
    }
  }

  return false
}
